/**
 * Message Templates Service
 * Handles WhatsApp/Email message templates with variables
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';

export type TemplateCategory = 'first_touch' | 'follow_up' | 'nurture' | 'reactivate';
export type TemplateChannel = 'whatsapp' | 'email';
export type TemplateScopeType = 'global' | 'keep_warm' | 'campaign';

export interface MessageTemplate {
  id: string;
  workspace_id: string;
  name: string;
  category: TemplateCategory;
  channel: TemplateChannel;
  body: string;
  variables: string[];
  is_global: boolean;
  scope_type?: TemplateScopeType;
  scope_id?: string | null;
  created_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  workspace_id: string;
  name: string;
  category: TemplateCategory;
  channel?: TemplateChannel;
  body: string;
  variables?: string[];
  is_global?: boolean;
  scope_type?: TemplateScopeType;
  scope_id?: string | null;
}

export interface GetTemplatesOptions {
  category?: TemplateCategory;
  scopeType?: TemplateScopeType;
}

export class MessageTemplatesService {
  /**
   * Get templates for workspace (including global).
   * When scopeType is 'keep_warm', returns templates with scope_type in ('keep_warm', 'global').
   */
  static async getTemplates(
    workspaceId: string,
    categoryOrOptions?: TemplateCategory | GetTemplatesOptions
  ): Promise<MessageTemplate[]> {
    try {
      const options: GetTemplatesOptions =
        typeof categoryOrOptions === 'string'
          ? { category: categoryOrOptions }
          : categoryOrOptions ?? {};

      let query = supabase
        .from('message_templates')
        .select('*')
        .or(`workspace_id.eq.${workspaceId},is_global.eq.true`)
        .order('created_at', { ascending: false });

      if (options.category) {
        query = query.eq('category', options.category);
      }

      if (options.scopeType === 'keep_warm') {
        query = query.or('scope_type.eq.keep_warm,scope_type.eq.global');
      } else if (options.scopeType) {
        query = query.eq('scope_type', options.scopeType);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('[MessageTemplatesService] Error fetching templates', error);
        throw new Error(getSupabaseErrorMessage(error));
      }

      return (data || []) as MessageTemplate[];
    } catch (error: any) {
      logger.error('[MessageTemplatesService] Failed to get templates', error);
      throw error;
    }
  }

  /**
   * Create template
   */
  static async createTemplate(input: CreateTemplateInput): Promise<MessageTemplate> {
    try {
      const scopeType = input.scope_type ?? 'keep_warm';
      const payload: Record<string, unknown> = {
        workspace_id: input.workspace_id,
        name: input.name,
        category: input.category,
        channel: input.channel || 'whatsapp',
        body: input.body,
        variables: input.variables ?? [],
        is_global: input.is_global ?? (scopeType === 'global'),
        scope_type: scopeType,
      };
      if (input.scope_id != null) (payload as any).scope_id = input.scope_id;

      const { data, error } = await supabase
        .from('message_templates')
        .insert(payload)
        .select()
        .single();

      if (error) {
        logger.error('[MessageTemplatesService] Error creating template', error);
        throw new Error(getSupabaseErrorMessage(error));
      }

      return data as MessageTemplate;
    } catch (error: any) {
      logger.error('[MessageTemplatesService] Failed to create template', error);
      throw error;
    }
  }

  /**
   * Update template
   */
  static async updateTemplate(
    templateId: string,
    updates: Partial<CreateTemplateInput>
  ): Promise<MessageTemplate> {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', templateId)
        .select()
        .single();

      if (error) {
        logger.error('[MessageTemplatesService] Error updating template', error);
        throw new Error(getSupabaseErrorMessage(error));
      }

      return data as MessageTemplate;
    } catch (error: any) {
      logger.error('[MessageTemplatesService] Failed to update template', error);
      throw error;
    }
  }

  /**
   * Delete template
   */
  static async deleteTemplate(templateId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('message_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        logger.error('[MessageTemplatesService] Error deleting template', error);
        throw new Error(getSupabaseErrorMessage(error));
      }
    } catch (error: any) {
      logger.error('[MessageTemplatesService] Failed to delete template', error);
      throw error;
    }
  }

  /**
   * Render template with variables
   */
  static renderTemplate(
    template: MessageTemplate,
    variables: Record<string, string>
  ): string {
    let rendered = template.body;

    // Replace variables: {first_name}, {company}, etc.
    template.variables.forEach((varName) => {
      const value = variables[varName] || '';
      const regex = new RegExp(`\\{${varName}\\}`, 'g');
      rendered = rendered.replace(regex, value);
    });

    return rendered;
  }
}

